import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DeleteIcon from "@mui/icons-material/Delete";
import { useState } from "react";
import { Link as RouterLink, useParams } from "react-router";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import {
  useDeleteQuestion,
  useQuestionTypes,
  useReorderQuestions,
  useSaveQuestions,
  useSurveyDetail,
  type SurveyQuestion,
} from "./api";
import { Authority } from "../../auth/authorities";
import { useAuth } from "../../auth/useAuth";
import { useNotify } from "../../app/useNotify";
import { errorMessageFor } from "../../api/client";
import {
  CHOICE_TYPES,
  questionTypeHint,
  questionTypeLabel,
  surveyStatusLabel,
} from "../../lib/labels";
import { usePageTitle } from "../../app/usePageTitle";

export function SurveyQuestionsPage() {
  const { surveyId: param } = useParams();
  const surveyId = Number(param);
  const { can } = useAuth();
  const notify = useNotify();

  const detail = useSurveyDetail(Number.isFinite(surveyId) ? surveyId : null);
  usePageTitle(detail.data?.surveyName ?? "Survey");
  const types = useQuestionTypes();
  const save = useSaveQuestions(surveyId);
  const reorder = useReorderQuestions(surveyId);
  const remove = useDeleteQuestion(surveyId);

  const [draft, setDraft] = useState<SurveyQuestion[] | null>(null);
  const [deleting, setDeleting] = useState<SurveyQuestion | null>(null);
  const [newText, setNewText] = useState("");
  const [newTypeId, setNewTypeId] = useState<number>(1);
  const [newOptions, setNewOptions] = useState("");

  const saved = detail.data?.questions ?? [];
  const questions = draft ?? saved;
  const dirty = draft !== null;
  const locked = ["PUBLISHED", "COMPLETED", "CANCELLED"].includes(detail.data?.status ?? "");
  const editable = can(Authority.EditSurvey) && !locked;

  const typeById = new Map((types.data ?? []).map((type) => [type.id, type]));
  const newTypeNeedsOptions = CHOICE_TYPES.has(typeById.get(newTypeId)?.type ?? "");
  const typeLabel = (question: SurveyQuestion) => {
    const id = question.questionType?.id ?? question.questionTypeId;
    return questionTypeLabel(typeById.get(id ?? -1)?.type ?? question.questionType?.type);
  };

  function move(index: number, delta: number) {
    const next = [...questions];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setDraft(next);
  }

  function addQuestion() {
    const type = typeById.get(newTypeId);
    if (!type) return;
    const options = newOptions
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .map((option) => ({
        description: null,
        id: null,
        option,
        optionType: null,
        questionId: null,
      }));
    setDraft([
      ...questions,
      {
        question: newText.trim(),
        questionType: { id: type.id, type: type.type },
        displayOrder: questions.length + 1,
        options,
      },
    ]);
    setNewText("");
    setNewOptions("");
  }

  if (detail.isLoading || types.isLoading) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box>
          <Typography variant="h5" component="h2">
            {detail.data?.surveyName}
          </Typography>
          <Typography color="text.secondary">{detail.data?.description}</Typography>
        </Box>
        <Stack direction="row" sx={{ gap: 1, alignItems: "center" }}>
          <Chip label={surveyStatusLabel(detail.data?.status)} size="small" />
          <Button component={RouterLink} to="/dashboard/survey/survey-list">
            Back to surveys
          </Button>
        </Stack>
      </Stack>

      {locked && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This survey is {surveyStatusLabel(detail.data?.status).toLowerCase()}, so its questions
          can no longer be changed.
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        {questions.length === 0 && (
          <Typography color="text.secondary" sx={{ py: 2 }}>
            No questions yet. Add the first one below.
          </Typography>
        )}
        {questions.map((question, index) => (
          <Stack
            key={question.id ?? `draft-${index}`}
            direction="row"
            sx={{
              alignItems: "flex-start",
              gap: 2,
              py: 1,
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Typography sx={{ minWidth: 28, pt: 1 }}>{index + 1}.</Typography>
            <Box sx={{ flexGrow: 1 }}>
              <Typography>{question.question}</Typography>
              <Stack
                direction="row"
                sx={{ gap: 1, mt: 0.5, alignItems: "center", flexWrap: "wrap" }}
              >
                <Chip size="small" label={typeLabel(question)} />
                {(question.options ?? []).map((option, optionIndex) => (
                  <Chip key={optionIndex} size="small" variant="outlined" label={option.option} />
                ))}
              </Stack>
            </Box>
            {editable && (
              <Stack direction="row">
                <IconButton
                  size="small"
                  aria-label={`Move question ${index + 1} up`}
                  onClick={() => move(index, -1)}
                >
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label={`Move question ${index + 1} down`}
                  onClick={() => move(index, 1)}
                >
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    aria-label={`Delete question ${index + 1}`}
                    onClick={() => {
                      if (question.id === undefined) {
                        setDraft(questions.filter((_, i) => i !== index));
                      } else {
                        setDeleting(question);
                      }
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            )}
          </Stack>
        ))}
      </Paper>

      {editable && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
            Add a question
          </Typography>
          <TextField
            label="Question"
            required
            value={newText}
            onChange={(event) => setNewText(event.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            select
            label="Answer type"
            value={newTypeId}
            onChange={(event) => setNewTypeId(Number(event.target.value))}
            fullWidth
            margin="normal"
          >
            {(types.data ?? []).map((type) => (
              <MenuItem key={type.id} value={type.id}>
                {questionTypeLabel(type.type)}
              </MenuItem>
            ))}
          </TextField>
          {questionTypeHint(typeById.get(newTypeId)?.type) && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: -1, mb: 1 }}>
              {questionTypeHint(typeById.get(newTypeId)?.type)}
            </Typography>
          )}
          {newTypeNeedsOptions && (
            <TextField
              label="Options"
              required
              value={newOptions}
              onChange={(event) => setNewOptions(event.target.value)}
              helperText="One per line."
              fullWidth
              multiline
              minRows={3}
              margin="normal"
            />
          )}
          <Button
            variant="outlined"
            sx={{ mt: 1 }}
            disabled={!newText.trim() || (newTypeNeedsOptions && !newOptions.trim())}
            onClick={addQuestion}
          >
            Add question
          </Button>
        </Paper>
      )}

      {editable && (
        <Stack direction="row" sx={{ gap: 2 }}>
          <Button
            variant="contained"
            disabled={!dirty || save.isPending}
            onClick={async () => {
              try {
                // New questions are posted with the whole set; an order-only
                // change goes to the order endpoint instead.
                const hasNew = questions.some((question) => question.id === undefined);
                if (hasNew) await save.mutateAsync(questions);
                else await reorder.mutateAsync(questions);
                setDraft(null);
                notify.success("Questions saved");
              } catch (error) {
                notify.error(errorMessageFor(error) ?? "Could not save the questions.");
              }
            }}
          >
            Save questions
          </Button>
          {dirty && <Button onClick={() => setDraft(null)}>Discard changes</Button>}
        </Stack>
      )}

      <ConfirmDialog
        open={deleting !== null}
        title={`Delete "${deleting?.question}"?`}
        message="Any answers already given to it are removed too. This cannot be undone."
        confirmLabel="Delete"
        destructive
        busy={remove.isPending}
        onClose={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting?.id) return;
          try {
            await remove.mutateAsync(deleting.id);
            setDraft(null);
            notify.success("Question deleted");
          } catch (error) {
            notify.error(errorMessageFor(error) ?? "Could not delete the question.");
          } finally {
            setDeleting(null);
          }
        }}
      />
    </Box>
  );
}
